/**
 * Keeps the photos of an unfinished create-by-voice capture in IndexedDB (native API, no library) so
 * they survive an app switch, the camera hand-off or a WebView reload. Photos only: audio is never
 * stored. Every call is best effort; a missing or blocked IndexedDB just means nothing is restored.
 */

const DB_NAME = "nine-voice-create";
const STORE = "session";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredSession {
  scope: string;
  files: File[];
  updatedAt: number;
}

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE, { keyPath: "scope" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return open().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const transaction = db.transaction(STORE, mode);
          const request = work(transaction.objectStore(STORE));
          transaction.oncomplete = () => {
            db.close();
            resolve(request.result ?? null);
          };
          transaction.onerror = transaction.onabort = () => {
            db.close();
            resolve(null);
          };
        } catch {
          db.close();
          resolve(null);
        }
      }),
  );
}

const VoiceCreateSessionStore = {
  save(scope: string, files: File[]): Promise<unknown> {
    if (!files.length) return VoiceCreateSessionStore.clear(scope);
    const session: StoredSession = { scope, files, updatedAt: Date.now() };
    return run("readwrite", (store) => store.put(session));
  },
  async load(scope: string): Promise<File[]> {
    const session = (await run<StoredSession>("readonly", (store) =>
      store.get(scope),
    )) as StoredSession | null;
    if (!session || Date.now() - session.updatedAt > MAX_AGE_MS) {
      if (session) void VoiceCreateSessionStore.clear(scope);
      return [];
    }
    return Array.isArray(session.files) ? session.files : [];
  },
  clear(scope: string): Promise<unknown> {
    return run("readwrite", (store) => store.delete(scope));
  },
  /** On logout: no photos survive for the next person on this device. */
  clearAll(): Promise<unknown> {
    return run("readwrite", (store) => store.clear());
  },
};

export default VoiceCreateSessionStore;
